/* ============================================================================
   Kawaii header (sections/kc-header.liquid)

   1. Sticky: the header stays stuck to the top, slides away while the page
      scrolls down and comes back on the way up, but never while a menu, the
      search, a phone dialog or the cart drawer is open. The slide is CSS on
      `top` (section-kc-header.css), never a transform: the cart drawer
      inside is position: fixed and would be trapped by one.
   2. Desktop menus (Shop, Characters) and the search panel: one open at a
      time, a scrim behind, closed by Escape, the scrim, their own button or
      focus leaving; Escape hands focus back. Their insides are <template>s,
      stamped in on first hover or focus. The polaroid piles in them ("New
      this week", the featured collection, the seasonal pick) refill from
      their whole collection when stamped in (kawaii-catalog.js), start on a
      random product and take turns while their menu is open; a featured
      collection is one of its list at random, under a random heading.
      A pile's magnifying glass opens the product's quick view on top; the
      menu stays open behind it and the pile holds still until it closes.
   3. Phone menu and phone search: modal <dialog>s, which bring the focus
      trap, Escape and the inert page; the page behind stops scrolling. The
      menu is a drawer over the page and slides back out when closed.
   4. Search: Shopify's predictive search JSON, debounced, stale requests
      aborted, answers cached per query. Each result carries the round
      quick-view magnifying glass (like snippets/kc-qv-button.liquid), not
      an add button: shoppers look first. The picks shown before anything
      is typed are drawn from their whole collection (loadPicks).
   5. Cart: whenever the drawer opens everything here closes (a modal
      dialog would sit on top of it).
   ============================================================================ */
(() => {
  if (customElements.get('kc-header')) return;

  const DESKTOP = '(min-width: 1100px)';
  const LIMIT = 8;
  const DEBOUNCE = 180;
  const DEAL_EVERY = 4500;
  const DEAL_OUT = 450;
  const MENU_OUT = 260;
  const stillMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const suggestCache = new Map();

  const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (c) => ESCAPES[c]);

  // Lower case without accents, so "pokemon" finds "Pokémon".
  const fold = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '');

  const fill = (text, values) =>
    String(text || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const number = (value) => Number(value || 0).toLocaleString('en-US');

  // The product quick view (kawaii-quick-view.js), which a pile's
  // magnifying glass opens over its menu.
  const inQuickView = (el) => !!(el && el.closest && el.closest('#kc-qv'));
  const quickViewOpen = () => !!document.querySelector('#kc-qv[open]');

  const sized = (url, width) => {
    if (!url || /[?&]width=/.test(url)) return url || '';
    return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
  };

  // Prices arrive as "29.99"; the theme formats cents with the shop's format.
  function money(amount) {
    const cents = Math.round(parseFloat(amount) * 100);
    if (!Number.isFinite(cents)) return '';
    const format = window.theme && theme.shopSettings && theme.shopSettings.moneyFormat;
    if (format && theme.Currency && theme.Currency.formatMoney) {
      return theme.Currency.formatMoney(cents, format).replace(/<[^>]*>/g, '');
    }
    return (cents / 100).toFixed(2);
  }

  // Every typed word has to start a word of the name: "hello kit" finds Hello Kitty.
  function matches(query, name) {
    const words = fold(query).split(/\s+/).filter(Boolean);
    if (!words.length) return false;
    const parts = fold(name).split(/[^a-z0-9]+/).filter(Boolean);
    return words.every((word) => parts.some((part) => part.startsWith(word)));
  }

  // The typed part of a suggestion, marked.
  function highlight(text, query) {
    const at = query ? fold(text).indexOf(fold(query)) : -1;
    if (at < 0) return esc(text);
    const end = at + query.length;
    return `${esc(text.slice(0, at))}<mark>${esc(text.slice(at, end))}</mark>${esc(text.slice(end))}`;
  }

  function normalise(json) {
    const results = (json && json.resources && json.resources.results) || {};
    return {
      products: (results.products || []).map((p) => {
        const varies = p.price_min && p.price_max && p.price_min !== p.price_max;
        return {
          title: p.title,
          url: p.url,
          image: (p.featured_image && p.featured_image.url) || p.image || '',
          price: (varies ? 'from ' : '') + money(varies ? p.price_min : p.price)
        };
      }),
      collections: (results.collections || []).map((c) => ({ title: c.title, url: c.url, handle: c.handle })),
      queries: (results.queries || []).map((s) => ({ text: s.text }))
    };
  }

  function suggest(endpoint, query, signal) {
    const key = fold(query);
    if (suggestCache.has(key)) return Promise.resolve(suggestCache.get(key));

    const params = new URLSearchParams();
    params.set('q', query);
    params.set('resources[type]', 'product,collection,query');
    params.set('resources[limit]', String(LIMIT));
    params.set('resources[limit_scope]', 'each');
    params.set('resources[options][unavailable_products]', 'last');

    return fetch(`${endpoint}?${params}`, { signal, headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Search answered ${response.status}`);
        return response.json();
      })
      .then((json) => {
        const result = normalise(json);
        suggestCache.set(key, result);
        return result;
      });
  }

  /* --------------------------------------------------------------------------
     A search view: the desktop panel or the phone dialog. Same data, two
     drawings of it, as on the canvas.
     -------------------------------------------------------------------------- */
  class KcSearch {
    constructor(host, mode, input, out, status) {
      this.host = host;
      this.mode = mode;
      this.input = input;
      this.out = out;
      this.status = status;
      this.query = '';
      this.result = null;
      this.failed = false;
      this.rendered = false;
      this.timer = 0;
      this.controller = null;
      this.onInput = this.onInput.bind(this);
      input.addEventListener('input', this.onInput);
    }

    destroy() {
      clearTimeout(this.timer);
      this.abort();
      this.input.removeEventListener('input', this.onInput);
    }

    activate() {
      this.host.loadPicks();
      if (!this.rendered) this.render();
    }

    setQuery(value) {
      this.input.value = value;
      this.input.focus();
      this.onInput(true);
    }

    onInput(now) {
      const typed = this.input.value;
      const query = typed.trim();
      if (this.mode === 'phone' && this.host.clearButton) this.host.clearButton.hidden = !typed;
      clearTimeout(this.timer);

      if (!query) {
        this.abort();
        this.query = '';
        this.result = null;
        this.failed = false;
        this.render();
        return;
      }
      if (query === this.query && this.result && !this.controller) return;
      this.timer = setTimeout(() => this.run(query), now === true ? 0 : DEBOUNCE);
    }

    abort() {
      if (this.controller) this.controller.abort();
      this.controller = null;
      this.busy(false);
    }

    busy(on) {
      const area = this.mode === 'desk' ? this.out.main : this.out.body;
      if (on) area.setAttribute('aria-busy', 'true');
      else area.removeAttribute('aria-busy');
    }

    async run(query) {
      this.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.busy(true);

      let result;
      let failed = false;
      try {
        result = await suggest(this.host.data.suggest, query, controller.signal);
      } catch (error) {
        if (controller.signal.aborted) return;
        result = { products: [], collections: [], queries: [] };
        failed = true;
      }
      if (this.controller !== controller) return;

      this.controller = null;
      this.busy(false);
      this.query = query;
      this.result = result;
      this.failed = failed;
      this.render();
    }

    // While the new pages are previewed (?view=kawaii), results open in
    // the new search page too.
    searchUrl(query) {
      const view = this.host.data.view ? `&view=${encodeURIComponent(this.host.data.view)}` : '';
      return `${this.host.data.search}?q=${encodeURIComponent(query)}&options%5Bprefix%5D=last${view}`;
    }

    model() {
      const { text, chars, picks } = this.host;
      const phone = this.mode === 'phone';
      const query = this.query;
      const has = Boolean(query && this.result);
      const products = has ? this.result.products : [];
      const same = fold(query).trim();

      const m = {
        query,
        suggestions: has ? this.result.queries.filter((s) => fold(s.text).trim() !== same).slice(0, phone ? 4 : 5) : [],
        chars: query ? chars.filter((c) => matches(query, c.n)) : [],
        collections: has ? this.result.collections.slice(0, 4).map((c) => this.host.collectionInfo(c)) : [],
        count: products.length
      };

      const found = products.length && products.length < LIMIT ? fill(text.found, { count: products.length }) : text.products;

      if (phone) {
        const nothing = has && !products.length && !m.chars.length && !m.collections.length && !m.suggestions.length;
        m.faces = (query && !nothing ? m.chars : chars).slice(0, 8);
        m.products = has ? products.slice(0, 5) : picks.slice(0, 4);
        m.heading = query ? found : text.picksPhone;
        m.showTerms = !query || nothing;
        m.empty = nothing ? fill(text.nonePhone, { q: query }) : '';
        m.charLink = has && !products.length && m.chars.length ? m.chars[0] : null;
        m.seeAll = has && !nothing && !m.charLink;
      } else {
        m.chars = m.chars.slice(0, 3);
        m.products = has ? products.slice(0, LIMIT) : picks.slice(0, LIMIT);
        m.heading = query ? found : text.picks;
        m.showTerms = !query || m.suggestions.length + m.chars.length + m.collections.length === 0;
        m.empty = has && !products.length && !m.chars.length ? fill(text.none, { q: query }) : '';
        m.charLink = has && !products.length && m.chars.length ? m.chars[0] : null;
        m.seeAll = has && products.length > 0;
      }

      if (!query) m.status = '';
      else if (this.failed) m.status = text.error;
      else if (products.length) m.status = fill(products.length === 1 ? text.statusOne : text.status, { count: products.length, q: query });
      else m.status = fill(text.statusNone, { q: query });

      return m;
    }

    render() {
      this.rendered = true;
      const m = this.model();
      if (this.mode === 'desk') this.renderDesk(m);
      else this.renderPhone(m);
      if (this.status) this.status.textContent = m.status;
    }

    countText(count) {
      const text = this.host.text;
      return count === 1 ? text.countOne : fill(text.count, { count: number(count) });
    }

    // The round quick-view button on a result, as snippets/kc-qv-button
    // draws it: a link to the product that kawaii-quick-view.js opens in
    // its pop-up from 990px (phones go to the page).
    qvButton(p, cls) {
      const { text, icons } = this.host;
      const qv = text.quickViewOn === true;
      const label = qv ? `${text.quickView}: ${p.title}` : p.title;
      return `<a class="kc-rnd kc-qv-rnd ${cls}" href="${esc(p.url)}" aria-label="${esc(label)}"${qv ? ' aria-haspopup="dialog" data-kc-qv' : ''}>${icons.search}</a>`;
    }

    termChips(cls) {
      return this.host.terms
        .map((term) => `<button class="${cls}" type="button" data-kc-term="${esc(term.toLowerCase())}">${esc(term)}</button>`)
        .join('');
    }

    renderDesk(m) {
      const { text, icons } = this.host;
      const side = [];

      if (m.suggestions.length) {
        side.push(`<div><p class="kc-nav__h">${esc(text.suggestions)}</p>${m.suggestions
          .map((s) => `<button class="kc-nav__sug" type="button" data-kc-term="${esc(s.text)}">${icons.search}<span>${highlight(s.text, m.query)}</span>${icons.up}</button>`)
          .join('')}</div>`);
      }
      if (m.chars.length) {
        side.push(`<div><p class="kc-nav__h">${esc(text.characters)}</p><div class="kc-nav__chms">${m.chars
          .map((c) => `<a class="kc-nav__chm" href="${esc(c.u)}"><span class="kc-nav__av">${c.i ? `<img src="${esc(c.i)}" alt="" width="48" height="48">` : ''}</span><span><b>${esc(c.n)}</b><small>${esc(this.countText(c.c))}</small></span></a>`)
          .join('')}</div></div>`);
      }
      if (m.collections.length) {
        side.push(`<div><p class="kc-nav__h">${esc(text.collections)}</p><div class="kc-nav__chips">${m.collections
          .map((c) => `<a class="kc-nav__chip" href="${esc(c.url)}">${esc(c.label)}${c.count ? `<small>${number(c.count)}</small>` : ''}</a>`)
          .join('')}</div></div>`);
      }
      if (m.showTerms && this.host.terms.length) {
        side.push(`<div><p class="kc-nav__h">${esc(text.terms)}</p><div class="kc-nav__chips">${this.termChips('kc-nav__chip')}</div></div>`);
      }
      this.out.side.innerHTML = side.join('');

      let main = `<div class="kc-nav__sp-mh"><p class="kc-nav__h">${esc(m.heading)}</p></div>`;
      if (m.products.length) {
        main += `<div class="kc-nav__pgrid">${m.products
          .map((p) => `<div class="kc-nav__res" data-kc-qv-zone><div class="kc-nav__res-media"><a class="kc-nav__res-img" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${p.image ? `<img src="${esc(sized(p.image, 360))}" alt="" width="360" height="360">` : ''}<span class="kc-ptag kc-nav__ptag"><span class="kc-ptag__in">${esc(p.price)}</span></span></a>${this.qvButton(p, 'kc-nav__add')}</div><a class="kc-nav__res-nm" href="${esc(p.url)}">${esc(p.title)}</a><span class="kc-vh">${esc(p.price)}</span></div>`)
          .join('')}</div>`;
      }
      if (m.empty) main += `<p class="kc-nav__empty">${esc(m.empty)}</p>`;
      if (m.charLink) {
        const label = fill(text.seeChar, { count: number(m.charLink.c), name: m.charLink.n });
        main += `<a class="kc-nav__seeall" href="${esc(m.charLink.u)}"><span>${esc(label)}</span>${icons.arrow}</a>`;
      }
      if (m.seeAll) {
        main += `<a class="kc-nav__seeall" href="${esc(this.searchUrl(m.query))}"><span>${esc(fill(text.seeAll, { q: m.query }))}</span>${icons.arrow}</a>`;
      }
      this.out.main.innerHTML = main;
    }

    renderPhone(m) {
      const { text, icons } = this.host;
      const head = (title) => `<h2 class="kc-nav__mh kc-nav__mh--s">${esc(title)}</h2>`;
      let html = '';

      if (m.empty) html += `<p class="kc-nav__mempty">${esc(m.empty)}</p>`;
      if (m.showTerms && this.host.terms.length) {
        html += `<div class="kc-nav__sgrp">${head(text.termsPhone)}<div class="kc-nav__mchips">${this.termChips('kc-nav__mchip')}</div></div>`;
      }
      if (m.faces.length) {
        html += `<div class="kc-nav__sgrp"><div class="kc-nav__msh">${head(text.characters)}<a class="kc-nav__mall" href="${esc(text.allCharsUrl)}">${esc(text.allChars)}${icons.arrow}</a></div><div class="kc-nav__mchars">${m.faces
          .map((c) => `<a class="kc-nav__mch" href="${esc(c.u)}"><span class="kc-nav__mface">${c.i ? `<img src="${esc(c.i)}" alt="" width="64" height="64" loading="lazy">` : ''}<span class="kc-ring" aria-hidden="true"></span></span>${esc(c.n)}</a>`)
          .join('')}</div></div>`;
      }
      if (m.collections.length) {
        html += `<div class="kc-nav__sgrp">${head(text.collections)}<div class="kc-nav__mchips">${m.collections
          .map((c) => `<a class="kc-nav__mchip" href="${esc(c.url)}">${esc(c.label)}${c.count ? `<small>${number(c.count)}</small>` : ''}</a>`)
          .join('')}</div></div>`;
      }
      if (m.suggestions.length) {
        html += `<div class="kc-nav__sgrp">${head(text.suggestions)}<div class="kc-nav__msugs">${m.suggestions
          .map((s) => `<button class="kc-nav__msug" type="button" data-kc-term="${esc(s.text)}">${icons.search}<span>${highlight(s.text, m.query)}</span>${icons.up}</button>`)
          .join('')}</div></div>`;
      }
      if (m.products.length) {
        html += `<div class="kc-nav__sgrp">${head(m.heading)}<div class="kc-nav__prows">${m.products
          .map((p) => `<div class="kc-nav__mprow" data-kc-qv-zone>${p.image ? `<img src="${esc(sized(p.image, 360))}" alt="" width="60" height="60" loading="lazy">` : '<span class="kc-nav__mprow-ph"></span>'}<span class="kc-nav__mpt"><a href="${esc(p.url)}">${esc(p.title)}</a><span>${esc(p.price)}</span></span>${this.qvButton(p, 'kc-nav__padd')}</div>`)
          .join('')}</div></div>`;
      }
      if (m.charLink) {
        const label = fill(text.seeChar, { count: number(m.charLink.c), name: m.charLink.n });
        html += `<a class="kc-btn kc-nav__mmore" href="${esc(m.charLink.u)}"><span class="kc-ring" aria-hidden="true"></span><span class="kc-nav__mmore-t">${esc(label)}</span>${icons.arrow}</a>`;
      }
      if (m.seeAll) {
        html += `<a class="kc-btn kc-nav__mmore" href="${esc(this.searchUrl(m.query))}"><span class="kc-ring" aria-hidden="true"></span><span class="kc-nav__mmore-t">${esc(fill(text.seeAll, { q: m.query }))}</span>${icons.arrow}</a>`;
      }
      this.out.body.innerHTML = html;
    }
  }

  /* --------------------------------------------------------------------------
     A polaroid pile (snippets/kc-header-deck.liquid). One product on top;
     the next is laid out unseen behind it (.is-next) so its lazy photo has
     loaded by its turn, and the rest stay display: none until theirs. Set
     up while still in the stamped-in fragment, so the random first photo
     is the only one that starts loading.

     A pile made from a collection (data-feed) then swaps its products, the
     first few Liquid can see, for a draw from the whole collection
     (kawaii-catalog.js) that this shopper hasn't seen in this pile lately,
     even on an earlier visit. A pile already on screen keeps the one on
     top.
     -------------------------------------------------------------------------- */
  class KcDeck {
    constructor(root) {
      this.root = root;
      this.slides = Array.from(root.querySelectorAll('[data-kc-slide]'));
      this.auto = root.hasAttribute('data-autoplay');
      this.index = 0;
      this.timer = 0;
      this.playing = false;
      this.hover = false;
      this.focus = false;
      if (this.slides.length < 2) return;

      this.index = Math.floor(Math.random() * this.slides.length);
      this.slides.forEach((slide, i) => slide.classList.toggle('is-on', i === this.index));
      this.mark();

      root.addEventListener('pointerenter', (event) => {
        if (event.pointerType !== 'mouse') return;
        this.hover = true;
        this.schedule();
      });
      root.addEventListener('pointerleave', () => {
        this.hover = false;
        this.schedule();
      });
      root.addEventListener('focusin', () => {
        this.focus = true;
        this.schedule();
      });
      // A quick view opened from the pile holds it still until the focus
      // comes back to the magnifying glass that opened it.
      root.addEventListener('focusout', (event) => {
        if (root.contains(event.relatedTarget) || inQuickView(event.relatedTarget)) return;
        this.focus = false;
        this.schedule();
      });
      root.addEventListener('click', (event) => {
        if (event.target.closest('[data-kc-deal]')) this.show(this.index + 1);
      });

      this.refill();
    }

    refill() {
      const feed = this.root.dataset.feed;
      const catalog = window.kcCatalog;
      if (!feed || !catalog) return;
      catalog
        .load(feed)
        .then((products) => {
          const keep = this.playing ? this.slides[this.index] : null;
          const spots = this.slides.filter((slide) => slide !== keep);
          const avoid = keep ? [keep.querySelector('.kc-nav__pnm').getAttribute('href')] : [];
          const list = products.filter((p) => p.image);
          catalog.draw(`deck:${feed}`, list, spots.length, avoid).forEach((p, i) => this.fill(spots[i], p));
        })
        .catch(() => {});
    }

    // Put a feed product into a slide, as the deck snippet draws it.
    fill(slide, p) {
      const { sized } = window.kcCatalog;
      const price = (p.varies ? 'from ' : '') + p.price_label;
      const name = slide.querySelector('.kc-nav__pnm');
      const look = slide.querySelector('.kc-qv-rnd');
      if (look && name) {
        // "Quick view: <old name>" keeps its words, gets the new name.
        const label = look.getAttribute('aria-label') || '';
        const old = name.textContent;
        look.setAttribute('aria-label', (label.endsWith(old) ? label.slice(0, label.length - old.length) : '') + p.title);
        look.href = p.url;
      }
      if (name) {
        name.href = p.url;
        name.textContent = p.title;
      }
      const photo = slide.querySelector('.kc-nav__pimg');
      const img = photo && photo.querySelector('img');
      if (photo) photo.href = p.url;
      if (img) {
        img.srcset = `${sized(p.image, 120)} 120w, ${sized(p.image, 240)} 240w`;
        img.src = sized(p.image, 240);
        if (p.w && p.h) {
          img.width = 240;
          img.height = Math.round((240 * p.h) / p.w);
        }
      }
      const said = slide.querySelector('.kc-vh');
      if (said) said.textContent = price;
      const tag = slide.querySelector('.kc-ptag__in');
      if (tag) {
        tag.textContent = '';
        if (p.compare > p.price && p.compare_label) {
          const was = document.createElement('s');
          was.textContent = p.compare_label;
          tag.append(was);
        }
        tag.append(price);
      }
    }

    // A menu closed under the mouse never hears it leave; start afresh.
    // `delay` holds the first turn back, to keep it out of step with
    // another pile in the same menu.
    play(delay = 0) {
      this.playing = true;
      this.hover = this.root.matches(':hover');
      this.focus = this.root.contains(document.activeElement);
      this.schedule(delay);
    }

    stop() {
      this.playing = false;
      clearTimeout(this.timer);
    }

    // Held still while pointed at or focused, and never under reduced motion.
    schedule(delay = 0) {
      clearTimeout(this.timer);
      if (!this.auto || !this.playing || this.hover || this.focus || stillMotion.matches || this.slides.length < 2) return;
      this.timer = setTimeout(() => {
        if (!document.hidden) this.show(this.index + 1);
        this.schedule();
      }, DEAL_EVERY + delay);
    }

    show(target) {
      const count = this.slides.length;
      const next = ((target % count) + count) % count;
      if (count < 2 || next === this.index) return;

      const out = this.slides[this.index];
      const slide = this.slides[next];
      out.classList.remove('is-on', 'is-in');
      out.classList.add('is-out');
      clearTimeout(out.kcOut);
      out.kcOut = setTimeout(() => {
        out.classList.remove('is-out');
        this.mark();
      }, DEAL_OUT);

      slide.classList.remove('is-out', 'is-next');
      slide.classList.add('is-on', 'is-in');
      this.index = next;
      this.mark();
    }

    mark() {
      const after = this.slides[(this.index + 1) % this.slides.length];
      this.slides.forEach((slide) => {
        const waiting = slide === after && !slide.classList.contains('is-on') && !slide.classList.contains('is-out');
        slide.classList.toggle('is-next', waiting);
      });
    }
  }

  /* --------------------------------------------------------------------------
     The header
     -------------------------------------------------------------------------- */
  class KcHeader extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;

      this.section = this.closest('.shopify-section') || this;
      this.data = this.readData();
      this.text = this.data.text || {};
      this.chars = this.data.chars || [];
      this.terms = (this.data.terms || []).map((term) => String(term).trim()).filter(Boolean);
      this.picks = (this.data.picks || []).map((p) => ({
        title: p.t,
        url: p.u,
        image: p.i,
        price: p.p
      }));
      this.cols = new Map();
      (this.data.cols || []).forEach(([handle, label, count]) => {
        if (!this.cols.has(handle)) this.cols.set(handle, { label, count });
      });
      this.icons = this.readIcons();

      this.scrim = this.querySelector('[data-kc-scrim]');
      this.links = this.querySelector('.kc-nav__links');
      this.form = this.querySelector('[data-kc-sform]');
      this.input = this.form ? this.form.querySelector('input[name="q"]') : null;
      this.escButton = this.form ? this.form.querySelector('.kc-nav__sx') : null;
      this.panels = {};
      this.querySelectorAll('[data-kc-panel]').forEach((panel) => {
        this.panels[panel.dataset.kcPanel] = panel;
      });
      this.toggles = {};
      this.querySelectorAll('[data-kc-toggle]').forEach((button) => {
        this.toggles[button.dataset.kcToggle] = button;
      });
      this.menu = this.querySelector('dialog[data-kc-menu]');
      this.finder = this.querySelector('dialog[data-kc-sdlg]');
      this.menuButton = this.querySelector('[data-kc-menu-open]');
      this.searchButton = this.querySelector('.kc-nav__sbtn');
      this.clearButton = this.finder ? this.finder.querySelector('[data-kc-clear]') : null;
      this.decks = [];
      this.current = '';
      this.hiddenState = false;
      this.lastY = window.scrollY || 0;
      this.desktop = window.matchMedia(DESKTOP);

      const panel = this.panels.search;
      if (this.input && panel) {
        this.desk = new KcSearch(
          this,
          'desk',
          this.input,
          { side: panel.querySelector('[data-kc-s-side]'), main: panel.querySelector('[data-kc-s-main]') },
          panel.querySelector('[data-kc-s-status]')
        );
      }
      const phoneInput = this.finder ? this.finder.querySelector('input[name="q"]') : null;
      if (phoneInput) {
        this.phone = new KcSearch(
          this,
          'phone',
          phoneInput,
          { body: this.finder.querySelector('[data-kc-s-body]') },
          this.finder.querySelector('[data-kc-s-status]')
        );
      }

      this.listen();
      this.initCart();
      if (window.Shopify && Shopify.designMode) this.initEditor();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('keydown', this.onKeyDown);
      document.removeEventListener('ajaxProduct:added', this.onAdded);
      if (this.desktop.removeEventListener) this.desktop.removeEventListener('change', this.onMedia);
      else if (this.desktop.removeListener) this.desktop.removeListener(this.onMedia);
      if (this.onBlockSelect) {
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
        document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
        document.removeEventListener('shopify:section:select', this.onSectionSelect);
      }
      clearTimeout(this.shutTimer);
      this.decks.forEach((deck) => deck.stop());
      if (this.desk) this.desk.destroy();
      if (this.phone) this.phone.destroy();
      if (this.locked) {
        document.body.classList.remove('overflow-hidden');
        this.locked = false;
      }
      this.started = false;
    }

    readData() {
      const script = this.querySelector('script[data-kc-nav-data]');
      try {
        return JSON.parse(script ? script.textContent : '{}');
      } catch (error) {
        return {};
      }
    }

    // The search's picks (before anything's typed) start as the first few
    // products Liquid can see in their collection. Once the search is
    // reached for, a draw from the whole collection (kawaii-catalog.js)
    // that this shopper hasn't been shown lately replaces them.
    loadPicks() {
      const feed = this.data.picksFeed;
      const catalog = window.kcCatalog;
      if (this.picksAsked || !feed || !catalog) return;
      this.picksAsked = true;
      catalog
        .load(feed)
        .then((products) => {
          const picks = catalog.draw(`picks:${feed}`, products.filter((p) => p.image), LIMIT);
          if (!picks.length) return;
          this.picks = picks.map((p) => ({
            title: p.title,
            url: p.url,
            image: catalog.sized(p.image, 360),
            price: (p.varies ? 'from ' : '') + p.price_label
          }));
          [this.desk, this.phone].forEach((view) => {
            if (view && view.rendered && !view.query) view.render();
          });
        })
        .catch(() => {
          this.picksAsked = false;
        });
    }

    // Icon markup for drawn results comes from the kit's own kc-icon snippet.
    readIcons() {
      const icons = {};
      const template = this.querySelector('template[data-kc-nav-icons]');
      if (template) {
        template.content.querySelectorAll('[data-i]').forEach((el) => {
          icons[el.dataset.i] = el.innerHTML;
        });
      }
      return icons;
    }

    collectionInfo(collection) {
      const known = this.cols.get(collection.handle);
      return {
        url: collection.url,
        label: known ? known.label : collection.title,
        count: known ? known.count : 0
      };
    }

    listen() {
      this.onClick = this.onClick.bind(this);
      this.onPointerOver = this.onPointerOver.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onFocusIn = this.onFocusIn.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onMedia = () => this.closeAll(false);
      // A quick view adds without opening the cart drawer, so its menu stays.
      this.onAdded = () => {
        if (!quickViewOpen()) this.closeAll(false);
      };

      this.addEventListener('click', this.onClick);
      this.addEventListener('pointerover', this.onPointerOver);
      this.addEventListener('pointerdown', this.onPointerDown, { passive: true });
      this.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);
      document.addEventListener('keydown', this.onKeyDown);
      document.addEventListener('ajaxProduct:added', this.onAdded);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.desktop.addEventListener) this.desktop.addEventListener('change', this.onMedia);
      else if (this.desktop.addListener) this.desktop.addListener(this.onMedia);

      if (this.input) {
        const open = () => this.openPanel('search');
        this.input.addEventListener('click', open);
        this.input.addEventListener('input', open);
      }

      [this.menu, this.finder].forEach((dialog) => {
        if (!dialog) return;
        // A click on the backdrop (or the page showing past the menu)
        // lands on the dialog itself.
        dialog.addEventListener('click', (event) => {
          if (event.target === dialog) this.shut(dialog);
        });
        // Escape: let the menu slide out first.
        dialog.addEventListener('cancel', (event) => {
          if (dialog !== this.menu || stillMotion.matches) return;
          event.preventDefault();
          this.shut(dialog);
        });
        dialog.addEventListener('close', () => this.onDialogClose(dialog));
      });
    }

    /* --- clicks --------------------------------------------------------- */

    onClick(event) {
      const target = event.target;
      if (target === this.scrim) {
        this.closePanel(false);
        return;
      }
      const el = target.closest(
        '[data-kc-toggle], [data-kc-close], [data-kc-menu-open], [data-kc-search-open], [data-kc-dclose], [data-kc-clear], [data-kc-term]'
      );
      if (!el || !this.contains(el)) return;

      if (el.dataset.kcToggle) this.toggle(el.dataset.kcToggle);
      else if (el.hasAttribute('data-kc-close')) this.closePanel(true);
      else if (el.hasAttribute('data-kc-menu-open')) this.openMenu();
      else if (el.hasAttribute('data-kc-search-open')) this.openFinder();
      else if (el.hasAttribute('data-kc-dclose')) this.shut(el.closest('dialog'));
      else if (el.hasAttribute('data-kc-clear')) {
        if (this.phone) this.phone.setQuery('');
      } else if (el.dataset.kcTerm !== undefined) {
        const view = el.closest('dialog') ? this.phone : this.desk;
        if (view) view.setQuery(el.dataset.kcTerm);
      }
    }

    // Intent: stamp a menu in before its button is clicked.
    onPointerOver(event) {
      const target = event.target;
      if (!target.closest) return;
      const toggle = target.closest('[data-kc-toggle]');
      if (toggle) this.hydrate(this.panels[toggle.dataset.kcToggle]);
      else if (this.desk && this.form && this.form.contains(target)) this.desk.activate();
    }

    onPointerDown(event) {
      const target = event.target;
      if (!target.closest) return;
      if (target.closest('[data-kc-menu-open]')) this.hydrate(this.menu);
    }

    /* --- focus and keys ------------------------------------------------- */

    onFocusIn(event) {
      // Keyboard users tabbing into a hidden header get it back.
      this.setHidden(false);
      const target = event.target;
      const toggle = target.closest && target.closest('[data-kc-toggle]');
      if (toggle) this.hydrate(this.panels[toggle.dataset.kcToggle]);
      if (target === this.input && !this.suppress) this.openPanel('search');
    }

    onFocusOut(event) {
      const name = this.current;
      const next = event.relatedTarget;
      // A quick view opened from the menu keeps the menu open behind it.
      if (!name || !next || inQuickView(next)) return;
      const keep = name === 'search' ? [this.form, this.panels.search] : [this.panels[name], this.toggles[name]];
      if (keep.some((el) => el && el.contains(next))) return;
      this.closePanel(false);
    }

    onKeyDown(event) {
      // Escape in a quick view closes just the quick view.
      if (event.key !== 'Escape' || !this.current || inQuickView(event.target)) return;
      // Also keeps the search field's text, which Escape would clear.
      event.preventDefault();
      this.closePanel(true);
    }

    /* --- desktop panels ------------------------------------------------- */

    hydrate(root) {
      if (!root) return;
      root.querySelectorAll('template[data-kc-tpl]').forEach((template) => {
        const content = document.importNode(template.content, true);
        // Featured collection: keep one of the options at random and drop
        // the rest before any of their photos start loading.
        content.querySelectorAll('[data-kc-pick]').forEach((group) => {
          const options = Array.from(group.querySelectorAll(':scope > [data-kc-pick-one]'));
          const keep = options[Math.floor(Math.random() * options.length)];
          options.forEach((option) => {
            if (option === keep) option.hidden = false;
            else option.remove();
          });
        });
        // A heading with a list of lines says one of them at random.
        content.querySelectorAll('[data-kc-say]').forEach((el) => {
          let lines = [];
          try {
            lines = JSON.parse(el.dataset.kcSay);
          } catch (error) {
            return;
          }
          if (Array.isArray(lines) && lines.length) el.textContent = lines[Math.floor(Math.random() * lines.length)];
        });
        content.querySelectorAll('[data-kc-deck]').forEach((el) => this.decks.push(new KcDeck(el)));
        template.replaceWith(content);
      });
    }

    // Piles in the same menu take turns half a beat apart, never at once.
    playDecks(panel, on) {
      let n = 0;
      this.decks.forEach((deck) => {
        if (!panel.contains(deck.root)) return;
        if (on) deck.play((n++ * DEAL_EVERY) / 2);
        else deck.stop();
      });
    }

    toggle(name) {
      if (this.current === name) this.closePanel(true);
      else this.openPanel(name);
    }

    openPanel(name) {
      if (this.current === name) return;
      const panel = this.panels[name];
      if (!panel) return;
      if (this.current) this.closePanel(false);

      this.hydrate(panel);
      this.current = name;
      panel.hidden = false;
      if (this.scrim) this.scrim.hidden = false;
      this.setHidden(false);

      if (name === 'search') {
        if (this.links) this.links.classList.add('is-off');
        this.form.classList.add('is-open');
        if (this.escButton) this.escButton.hidden = false;
        if (this.input.dataset.placeholderOpen) this.input.placeholder = this.input.dataset.placeholderOpen;
        if (this.desk) this.desk.activate();
      } else {
        const toggle = this.toggles[name];
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'true');
          toggle.classList.add('is-on');
        }
        this.playDecks(panel, true);
      }
    }

    closePanel(returnFocus) {
      const name = this.current;
      if (!name) return;
      this.current = '';
      const panel = this.panels[name];
      if (panel) {
        panel.hidden = true;
        this.playDecks(panel, false);
      }
      if (this.scrim) this.scrim.hidden = true;

      if (name === 'search') {
        if (this.links) this.links.classList.remove('is-off');
        this.form.classList.remove('is-open');
        if (this.escButton) this.escButton.hidden = true;
        this.input.placeholder = this.input.dataset.placeholder || '';
        if (returnFocus) {
          this.suppress = true;
          this.input.focus();
          this.suppress = false;
        }
      } else {
        const toggle = this.toggles[name];
        if (toggle) {
          toggle.setAttribute('aria-expanded', 'false');
          toggle.classList.remove('is-on');
          if (returnFocus) toggle.focus();
        }
      }
    }

    /* --- phone dialogs -------------------------------------------------- */

    openMenu() {
      if (!this.menu || this.menu.open || typeof this.menu.showModal !== 'function') return;
      this.closePanel(false);
      this.hydrate(this.menu);
      this.menu.showModal();
      if (this.menuButton) this.menuButton.setAttribute('aria-expanded', 'true');
      this.lock();
      this.setHidden(false);
    }

    openFinder() {
      if (!this.finder || this.finder.open || typeof this.finder.showModal !== 'function') return;
      if (this.menu && this.menu.open) this.menu.close();
      this.closePanel(false);
      if (this.phone) this.phone.activate();
      this.finder.showModal();
      if (this.searchButton) this.searchButton.setAttribute('aria-expanded', 'true');
      this.lock();
      this.setHidden(false);
      if (this.phone) this.phone.input.focus();
    }

    // Closes a phone dialog. The menu slides back out first (Escape, the
    // backdrop and its close button all come through here); search, and
    // anything under reduced motion, closes at once.
    shut(dialog) {
      if (!dialog || !dialog.open) return;
      if (dialog !== this.menu || stillMotion.matches) {
        dialog.close();
        return;
      }
      if (dialog.classList.contains('is-closing')) return;
      dialog.classList.add('is-closing');
      clearTimeout(this.shutTimer);
      this.shutTimer = setTimeout(() => dialog.close(), MENU_OUT);
    }

    onDialogClose(dialog) {
      if (dialog === this.menu) {
        clearTimeout(this.shutTimer);
        dialog.classList.remove('is-closing');
      }
      // `close` fires a task later; the dialog may already be open again.
      if (dialog.open) return;
      if (dialog === this.menu && this.menuButton) {
        this.menuButton.setAttribute('aria-expanded', 'false');
      }
      if (dialog === this.finder && this.searchButton) {
        this.searchButton.setAttribute('aria-expanded', 'false');
        // Opened from inside the menu, the dialog can't hand focus back
        // to where it came from; the bar's search button takes it.
        const active = document.activeElement;
        if (this.searchButton.offsetParent && (!active || active === document.body || (this.menu && this.menu.contains(active)))) {
          this.searchButton.focus();
        }
      }
      this.lock();
    }

    // The theme's own scroll lock (body.overflow-hidden, padded by the
    // scrollbar's width) while either dialog is open.
    lock() {
      const open = Boolean((this.menu && this.menu.open) || (this.finder && this.finder.open));
      if (open && !this.locked) {
        if (typeof window.setScrollbarWidth === 'function') window.setScrollbarWidth();
        document.body.classList.add('overflow-hidden');
        this.locked = true;
      } else if (!open && this.locked) {
        document.body.classList.remove('overflow-hidden');
        this.locked = false;
      }
    }

    closeAll(returnFocus) {
      this.closePanel(returnFocus);
      if (this.menu && this.menu.open) this.menu.close();
      if (this.finder && this.finder.open) this.finder.close();
    }

    /* --- cart ----------------------------------------------------------- */

    initCart() {
      this.cartDetails = this.querySelector('cart-drawer > details');

      // The drawer opens from the bar, after any add to cart, or from apps.
      if (this.cartDetails) {
        this.cartDetails.addEventListener('toggle', () => {
          if (this.cartDetails.open) this.closeAll(false);
        });
      }
    }

    /* --- sticky --------------------------------------------------------- */

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateSticky();
      });
    }

    busy() {
      return (
        Boolean(this.current) ||
        Boolean(this.menu && this.menu.open) ||
        Boolean(this.finder && this.finder.open) ||
        /\bmini-cart--(open|opening|closing)\b/.test(document.body.className)
      );
    }

    updateSticky() {
      const y = Math.max(window.scrollY || 0, 0);
      if (this.busy()) {
        this.lastY = y;
        return;
      }
      if (y <= this.section.offsetHeight) {
        this.setHidden(false);
        this.lastY = y;
        return;
      }
      const delta = y - this.lastY;
      if (Math.abs(delta) < 8) return;
      this.setHidden(delta > 0);
      this.lastY = y;
    }

    setHidden(hidden) {
      if (this.hiddenState === hidden) return;
      this.hiddenState = hidden;
      this.section.classList.toggle('kc-nav-hidden', hidden);
    }

    /* --- theme editor --------------------------------------------------- */

    // Everything is stamped in straight away, and selecting a block opens
    // the menu that shows it.
    initEditor() {
      Object.keys(this.panels).forEach((name) => this.hydrate(this.panels[name]));
      this.hydrate(this.menu);

      this.onBlockSelect = (event) => {
        if (!this.contains(event.target)) return;
        const id = event.detail && event.detail.blockId;
        const found = Array.from(this.querySelectorAll('[data-kc-block]')).filter((el) => el.dataset.kcBlock === id);
        if (!found.length) return;
        this.setHidden(false);
        if (this.desktop.matches) {
          const names = found
            .map((el) => el.closest('[data-kc-panel]'))
            .filter(Boolean)
            .map((panel) => panel.dataset.kcPanel);
          const name = names.includes('chars') ? 'chars' : names[0];
          if (name) this.openPanel(name);
          else this.closePanel(false);
        } else {
          const inMenu = found.find((el) => this.menu && this.menu.contains(el));
          if (inMenu) {
            this.openMenu();
            inMenu.scrollIntoView({ block: 'center' });
          }
        }
      };
      this.onBlockDeselect = (event) => {
        if (this.contains(event.target)) this.closeAll(false);
      };
      this.onSectionSelect = (event) => {
        if (event.target === this.section || this.contains(event.target)) this.setHidden(false);
      };
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
      document.addEventListener('shopify:section:select', this.onSectionSelect);
    }
  }

  customElements.define('kc-header', KcHeader);
})();
